import React, { useState, useEffect } from "react";
import {
  Button as MuiButton,
  Paper,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { Tenant } from "../../app/models/tenant";
import DataTable from "../../app/common/DataTable";
import { useStore } from "../../app/stores/store";
import { User } from "../../app/models/user";
import ConfirmComponent from "../../app/common/form/ConfirmComponent";
import { UserRegisterDto } from "../../app/models/userRegisterDto";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import CustomTextInput from "../../app/common/form/CustomTextInput";
import CustomSelectInput from "../../app/common/form/CustomSelectInput";


export default function CreateUserForm() {
  const [formData, setFormData] = useState<UserRegisterDto>({
    username: "",
    email: "",
    password: "",
    tenantId: "",
  });

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const handleFormSubmit = async (values: UserRegisterDto) => {
    try {
      await userStore.register(values);
      resetFormData();
      console.log("User created successfully");
    } catch (error) {
      console.error("Error creating user:", error);
    }
  };

  const resetFormData = () => {
    setFormData({
      username: "",
      email: "",
      password: "",
      tenantId: "",
    });
  };

  const userStore = useStore((s) => s.userStore);
  const modalStore = useStore((s) => s.modalStore);
  const { loadUsers } = userStore;
  const formatDateTime = (value?: string) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return new Intl.DateTimeFormat("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  };

  const columns = [
    { key: "userName", header: "Korisničko ime" },
    { key: "email", header: "Email" },
    { key: "tenant.name", header: "Džemat" },
    { key: "phoneNumber", header: "Telefon" },
    {
      key: "lastLoginAt",
      header: "Posljednja prijava",
      render: (item: User) => formatDateTime(item.lastLoginAt),
    },
    {
      key: "emailConfirmed",
      header: "Email potvrđen",
      render: (item: User) => (item.emailConfirmed ? "Da" : "Ne"),
    },
    {
      key: "accessFailedCount",
      header: "Neuspjele prijave",
      render: (item: User) => String(item.accessFailedCount ?? 0),
    },
    {
      key: "lockoutEnd",
      header: "Zaključan do",
      render: (item: User) => formatDateTime(item.lockoutEnd),
    },
  ];

  const handleDelete = async (id: string) => {
    await userStore.deleteUser(id);
    loadUsers({});
    modalStore.closeModal();
  };

  const actionButtons = [
    {
      icon: <DeleteIcon />,
      color: "error",
      onClick: (event: React.MouseEvent, item: User) => {
        event.stopPropagation();
        modalStore.openModal(
          <ConfirmComponent
            onYes={async () => handleDelete(item.id!)}
            onCancel={modalStore.closeModal}
          />
        );
      },
    },
  ];

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const response = await axios.get("/tenants");
        setTenants(response.data);
      } catch (error) {
        console.error("Error fetching tenants:", error);
      }
    };
    fetchTenants();
    loadUsers({});
  }, []);

  const validationSchema = Yup.object({
    username: Yup.string().required("Korisničko ime je obavezno"),
    email: Yup.string()
      .email("Email adresa nije ispravna")
      .required("Email adresa je obavezna"),
    password: Yup.string()
      .required("Lozinka je obavezna")
      .min(8, "Lozinka mora sadržavati barem 8 znakova")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{8,}$/,
        "Lozinka mora sadržavati barem jedno malo i veliko slovo, jedan broj i jedan poseban znak"
      ),
    tenantId: Yup.string().required("Džemat je obavezan"),
  });

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          Dodaj administratora
        </Typography>
        <Formik
          initialValues={formData}
          validationSchema={validationSchema}
          onSubmit={(values) => handleFormSubmit(values)}
        >
          {({ handleSubmit, isValid, isSubmitting, dirty }) => (
            <Form onSubmit={handleSubmit} autoComplete="off">
              <div className="two fields">
                <CustomSelectInput
                  options={tenants.map((tenant) => ({
                    key: tenant.id,
                    value: tenant.id,
                    text: tenant.name,
                  }))}
                  name="tenantId"
                  placeholder="Odaberi džemat"
                  label="Odaberi džemat"
                />
                <CustomTextInput
                  name="email"
                  placeholder="Email"
                  label="Email"
                />
              </div>

              <div className="two fields">
                <CustomTextInput
                  name="username"
                  placeholder="Korisničko ime"
                  label="Korisničko ime"
                />

                <CustomTextInput
                  type="password"
                  name="password"
                  placeholder="Lozinka"
                  label="Lozinka"
                />
              </div>

              <MuiButton
                variant="contained"
                color="primary"
                fullWidth
                disabled={isSubmitting || !dirty}
                type="submit"
              >
                Kreiraj korisnika
              </MuiButton>
            </Form>
          )}
        </Formik>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <DataTable
          columns={columns}
          data={userStore.tableData}
          actionButtons={actionButtons}
          onFilter={userStore.loadUsers}
        />
      </Paper>
    </>
  );
}
